import {combineLatest, merge} from 'rxjs';
import {map, scan} from 'rxjs/operators';
import {marginCurrencies$, currencies$} from '@/config';
import {wsLogout$} from '@/user/auth-ws';
import {maoMsg$} from '@/raw/mao/index';
import {isUtaSpot$} from '@/user/user';
import type {ws, ReduceAction, MarginCurrencyEntity} from '@/types';
type MAOMsg = ws.MAOMsg;
type MarginAccountRaw = ws.MarginAccountRaw;

type Cache = Map<string, MarginAccountRaw>;
type Action = ReduceAction<Cache>;

export const innerCurrenciesCache$ = combineLatest([isUtaSpot$, currencies$, marginCurrencies$]).pipe(
  map(([isUtaSpot, currencies, marginCurrencies]): Map<string, MarginCurrencyEntity> => {
    const currenciesMap = marginCurrencies.reduce((p, c) => {
      p.set(c.currency, c);
      return p;
    }, new Map());

    if (!isUtaSpot) {
      return currenciesMap;
    }
    return currencies.reduce((p, c) => {
      if (currenciesMap.has(c.currency)) {
        return p;
      }
      p.set(c.currency, convertInitCollateralCurrency(c));
      return p;
    }, currenciesMap);
  })
);

export const innerCurrencies$ = innerCurrenciesCache$.pipe(map((o): MarginCurrencyEntity[] => o.toArray()));

const init$ = innerCurrencies$.pipe(
  map((currencies): Action => {
    return (cache: Cache) => {
      currencies.forEach((item) =>
        cache.get(item.currency) ? cache : cache.set(item.currency, convertInitAccount(item.currency))
      );
      return cache;
    };
  })
);

const reset$ = wsLogout$.pipe(
  map(
    (): Action => (cache: Cache) => {
      cache.clear();
      return cache;
    }
  )
);

const accountsData$ = maoMsg$.pipe(map((data: MAOMsg): MarginAccountRaw[] => data.wallets));

const action$ = combineLatest([accountsData$, innerCurrencies$]).pipe(
  map(([accounts, currencyEntities]: [MarginAccountRaw[], MarginCurrencyEntity[]]): Action => {
    const currencies = currencyEntities.map((o) => o.currency);
    return (cache: Cache) => {
      accounts
        .filter((o) => currencies.includes(o.currency))
        .forEach((account) => cache.set(account.currency, account));
      return cache;
    };
  })
);

export const marginAccountsCache$ = merge<Action[]>(init$, action$, reset$).pipe(
  scan((p: Cache, c: Action) => c(p), new Map<string, MarginAccountRaw>())
);

function convertInitAccount(currency): MarginAccountRaw {
  return {
    balanceEv: '0',
    fundEv: '0',
    currency,
    lastUpdateTimeNs: 0,
    lockedTradingBalanceEv: '0',
    lockedWithdrawEv: '0',
    borrowedEv: '0',
    cumInterestEv: '0',
    hourlyInterestRateEr: '0',
    maxAmountToBorrowEv: '0',
    riskLevelEr: '999',
    userID: 0,
  };
}

function convertInitCollateralCurrency(currencyInfo): MarginCurrencyEntity {
  return {
    currency: currencyInfo.currency,
    name: currencyInfo.name,
    displayCurrency: currencyInfo.displayCurrency,
    valuePrecision: currencyInfo.valuePrecision,
    pricePrecision: currencyInfo.pricePrecision,
    status: currencyInfo.status,
    valueFactor: 1,
    valueScale: 0,
    code: currencyInfo.code,
    leverage: 0,
    markDelete: true,
    coefficient: '0',
    defaultHourlyInterestRate: '0',
    defaultMaxAmountToBorrowPerAccount: '0',
    minAmountToBorrowPerAccount: '0', // larger than tickSize * hourlyInterestRate
    maxAmountToBorrow: '0',
    liqFeeRate: '0',
    adlTargetRiskLevel: '0',
    priceCoefficientInAdl: '1',
    canRepay: false,
    canBorrow: false,
    canTransferOut: true,
    canTransferIn: true,
    canTrade: true,
  };
}
